/**
 * Widerruf Service
 * Handles the "Vertrag widerrufen" form: loading the captcha and submitting
 * the form via Supabase Edge Functions. No direct client-side table queries.
 */

import { logger } from '../lib/utils';

const getEdgeFunctionUrl = (functionName: string): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not defined');
  }
  return `${supabaseUrl}/functions/v1/${functionName}`;
};

const getAnonKey = (): string => {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!anonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
  }
  return anonKey;
};

export interface CaptchaChallenge {
  token: string;
  svg: string;
}

export interface WiderrufFormData {
  firstName: string;
  lastName: string;
  email: string;
  orderNumber: string;
}

/**
 * Load a fresh captcha challenge (signed token + distorted SVG image).
 */
export const loadCaptcha = async (): Promise<CaptchaChallenge> => {
  const response = await fetch(getEdgeFunctionUrl('widerruf-captcha'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAnonKey()}`,
    },
  });

  const result = await response.json();

  if (!response.ok || !result.token || !result.svg) {
    throw new Error(result.error || 'Failed to load captcha');
  }

  return { token: result.token, svg: result.svg };
};

/**
 * Submit the Widerruf form. Returns { success } – on failure an error message
 * is included so the UI can point the user to /kontaktformular.
 */
export const submitWiderruf = async (
  data: WiderrufFormData,
  captchaToken: string,
  captchaAnswer: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(getEdgeFunctionUrl('submit-widerruf'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAnonKey()}`,
      },
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        orderNumber: data.orderNumber,
        captchaToken,
        captchaAnswer,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Submission failed');
    }

    return { success: true };
  } catch (error) {
    logger.error('Error submitting Widerruf:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
